import React from 'react';
import { classes, useRedirect } from '../../../lib';
import Styles from './BlogPage.module.less';
import {
    BLOG_POSTS_PAGE_SIZE,
    SanityObject,
    useSanity,
} from '../../../lib/landingPage';
import { observer } from 'mobx-react-lite';
import { Footer } from '../Footer';
import { FeaturedBlogPost } from '../FeaturedBlogPost';
import { Header } from '../Header';
import { BlogSubmenu } from '../BlogSubmenu';
import { BlogSearchForm } from '../../../components/landingPage/BlogSearchForm/BlogSearchForm';
import { CategoriesDropdown } from './CategoriesDropdown';
import { BlogPostCategory } from '../../../components/landingPage/BlogPost';
import { Button } from '../../../components/Button';
import { Await } from '../../../components/Await';
import routes from '../../../pages/landingPage/routes';
import { generatePath } from 'react-router-dom';
import _ from 'lodash';
import { SanityBlogPostPreview } from '../BlogPost/types';
import { BlogPostPreview } from '../BlogPostPreview';

type FeaturedPostProps = SanityObject<{
    featuredPost: SanityObject<SanityBlogPostPreview>;
}> | null;

export type BlogPostsPageProps = {
    pageTitle?: string;
    featuredPost?: FeaturedPostProps;
    categorySlug?: string;
    searchTerm?: string;
    categories: BlogPostCategory[];
};

const Posts = observer(
    ({
        posts,
        nextPage,
        isLastPage,
    }: {
        posts: SanityObject<SanityBlogPostPreview>[];
        nextPage: () => void;
        isLastPage: boolean;
    }) => {
        return (
            <>
                {posts.length > 0 && (
                    <div className={classes(Styles.blogGrid)}>
                        <BlogPostPreview
                            value={posts.slice(0, BLOG_POSTS_PAGE_SIZE)}
                        />
                    </div>
                )}
                {isLastPage && posts.length > BLOG_POSTS_PAGE_SIZE && (
                    <div className="horizontal center-middle">
                        <Button onClick={nextPage} type="link">
                            Carregar mais...
                        </Button>
                    </div>
                )}
            </>
        );
    }
);

const PostsPage = observer(
    ({
        page,
        categorySlug,
        searchTerm,
        nextPage,
        isLastPage,
    }: {
        page: number;
        categorySlug?: string;
        searchTerm?: string;
        nextPage: () => void;
        isLastPage: boolean;
    }) => {
        const sanity = useSanity();
        return (
            <Await
                views={{
                    posts: sanity.asyncPosts(page, categorySlug, searchTerm),
                }}
                resolved={Posts}
                extra={{
                    nextPage: nextPage,
                    isLastPage: isLastPage,
                }}
            />
        );
    }
);

export const BlogPostsPage = observer(
    ({
        pageTitle,
        featuredPost,
        categorySlug,
        searchTerm,
        categories,
    }: BlogPostsPageProps) => {
        const redirect = useRedirect();
        const [pages, setPages] = React.useState(0);
        const onSearch = (searchTerm: string) => {
            return redirect(
                generatePath(routes.blog.search.path, { searchTerm })
            );
        };
        const onCategorySelect = (categorySlug: string) => {
            return redirect(
                generatePath(routes.blog.category.path, { categorySlug })
            );
        };
        return (
            <>
                <Header showSignupButton />
                <BlogSubmenu categories={categories} />
                <div
                    className={classes(
                        Styles.mainContainer,
                        'vertical between-middle mt-px16 mt-py24 mt-g24 d-px32 d-py48 d-g48'
                    )}>
                    {featuredPost && (
                        <FeaturedBlogPost {...featuredPost.featuredPost} />
                    )}
                    <h1>{pageTitle}</h1>
                    {!searchTerm && !categorySlug && (
                        <div className={Styles.search}>
                            <BlogSearchForm onSearch={onSearch} />
                            <div>
                                <CategoriesDropdown
                                    categories={categories}
                                    onSelect={(category: BlogPostCategory) => {
                                        onCategorySelect(category.slug);
                                    }}
                                />
                            </div>
                        </div>
                    )}
                    {_.range(pages + 1).map(p => (
                        <PostsPage
                            key={p}
                            page={p}
                            categorySlug={categorySlug}
                            searchTerm={searchTerm}
                            nextPage={() => setPages(pages + 1)}
                            isLastPage={p === pages}
                        />
                    ))}
                </div>
                <Footer hideMenu={false} />
            </>
        );
    }
);
